package fr.frux.feature.treedetail.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AddAPhoto
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import fr.frux.core.designsystem.a11y.rememberReduceMotion
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxRemoteImage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.designsystem.zoom.ZoomState
import fr.frux.core.designsystem.zoom.zoomable
import fr.frux.feature.treedetail.TreeDetailPhoto
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_photo_add
import fr.frux.feature.treedetail.resources.treedetail_photo_close_description
import fr.frux.feature.treedetail.resources.treedetail_photo_delete_description
import fr.frux.feature.treedetail.resources.treedetail_photo_delete_dialog_cancel
import fr.frux.feature.treedetail.resources.treedetail_photo_delete_dialog_confirm
import fr.frux.feature.treedetail.resources.treedetail_photo_delete_dialog_text
import fr.frux.feature.treedetail.resources.treedetail_photo_delete_dialog_title
import fr.frux.feature.treedetail.resources.treedetail_photo_empty
import fr.frux.feature.treedetail.resources.treedetail_photo_index
import fr.frux.feature.treedetail.resources.treedetail_photo_more_count
import fr.frux.feature.treedetail.resources.treedetail_photo_open_description
import fr.frux.feature.treedetail.resources.treedetail_photo_report_description
import fr.frux.feature.treedetail.resources.treedetail_photo_report_dialog_cancel
import fr.frux.feature.treedetail.resources.treedetail_photo_report_dialog_confirm
import fr.frux.feature.treedetail.resources.treedetail_photo_report_dialog_text
import fr.frux.feature.treedetail.resources.treedetail_photo_report_dialog_title
import fr.frux.feature.treedetail.resources.treedetail_photo_single_description
import fr.frux.feature.treedetail.resources.treedetail_photo_view_all_description
import fr.frux.feature.treedetail.resources.treedetail_photo_view_page_description
import fr.frux.feature.treedetail.resources.treedetail_species_photo_a11y
import fr.frux.feature.treedetail.resources.treedetail_species_photo_a11y_credit_author
import fr.frux.feature.treedetail.resources.treedetail_species_photo_a11y_credit_source
import fr.frux.feature.treedetail.resources.treedetail_species_photo_caption
import fr.frux.feature.treedetail.resources.treedetail_species_photo_credit_author
import fr.frux.feature.treedetail.resources.treedetail_species_photo_credit_source
import fr.frux.shared.domain.model.SpeciesPhoto
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

private val GRID_RADIUS = 20.dp
private val CELL_HEIGHT = 150.dp
private val GRID_HEIGHT = 240.dp

/**
 * Grille photos adaptative de la fiche arbre (spec Fiche arbre, variantes B — remplace l'ancien
 * hero pager `TreeGallery`) :
 * - 0 photo : bloc 240 dp `primaryContainer` avec bouton terracotta « Ajouter une photo » ;
 * - 1 photo : 2fr/1fr hauteur 240, case « Ajouter » pleine hauteur ;
 * - 2 photos : 2 colonnes × 2 rangées de 150, la première photo sur les deux rangées ;
 * - 3 et plus : la 3e case montre la 3e photo sous un voile `primary` avec « +N ». L'ajout
 *   devient un bouton Outline sous la grille.
 *
 * Toucher une photo, ou la case « +N », ouvre le pager plein écran sur cette photo (#686).
 *
 * Le drapeau (signaler) ou la corbeille (supprimer) — une seule action, selon les droits — reste
 * en haut à droite de la grande vignette ; les dialogues de confirmation ne changent pas.
 *
 * [photoUrl] reste le repli quand le transport n'a pas renseigné la galerie : une vignette seule,
 * sans droits attachés.
 */
@Composable
internal fun PhotoGrid(
    photos: List<TreeDetailPhoto>,
    photoUrl: String?,
    speciesGlyph: ImageVector?,
    speciesPhoto: SpeciesPhoto?,
    speciesName: String,
    isUploadingPhoto: Boolean,
    onAddPhotoClick: (() -> Unit)?,
    isReportingPhoto: Boolean,
    onReportPhotoClick: (String) -> Unit,
    isDeletingPhoto: Boolean,
    onDeletePhotoClick: (String) -> Unit,
    modifier: Modifier = Modifier,
    failedCount: Int = 0,
) {
    var pendingReportPhotoId by remember { mutableStateOf<String?>(null) }
    var pendingDeletePhotoId by remember { mutableStateOf<String?>(null) }
    var viewerStartPage by remember { mutableStateOf<Int?>(null) }

    // Repli d'avant la galerie : une vignette seule, sans identifiant ni droits.
    val displayPhotos =
        if (photos.isEmpty() && photoUrl != null) {
            listOf(TreeDetailPhoto(id = "", url = photoUrl, canDelete = false, canReport = false))
        } else {
            photos
        }

    Column(modifier = modifier.fillMaxWidth()) {
        when {
            displayPhotos.isEmpty() && failedCount > 0 -> {
                // La tuile « Pas envoyée » prend la place de l'ajout. Le renvoi passe par l'encart.
                Row(
                    modifier = Modifier.fillMaxWidth().height(GRID_HEIGHT),
                    horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    Box(Modifier.weight(2f).fillMaxHeight()) {
                        EmptyPhotoBlock(
                            speciesGlyph = speciesGlyph,
                            speciesPhoto = speciesPhoto,
                            speciesName = speciesName,
                            isUploadingPhoto = isUploadingPhoto,
                            onAddPhotoClick = null,
                        )
                    }
                    FailedPhotoTile(count = failedCount, modifier = Modifier.weight(1f).fillMaxHeight())
                }
            }

            displayPhotos.isEmpty() -> {
                EmptyPhotoBlock(
                    speciesGlyph = speciesGlyph,
                    speciesPhoto = speciesPhoto,
                    speciesName = speciesName,
                    isUploadingPhoto = isUploadingPhoto,
                    onAddPhotoClick = onAddPhotoClick,
                )
            }

            displayPhotos.size == 1 -> {
                SinglePhotoRow(
                    photo = displayPhotos[0],
                    failedCount = failedCount,
                    isUploadingPhoto = isUploadingPhoto,
                    onAddPhotoClick = onAddPhotoClick,
                    isReportingPhoto = isReportingPhoto,
                    isDeletingPhoto = isDeletingPhoto,
                    onReport = { pendingReportPhotoId = it },
                    onDelete = { pendingDeletePhotoId = it },
                    onOpenPhoto = { viewerStartPage = it },
                )
            }

            else -> {
                MultiPhotoGrid(
                    photos = displayPhotos,
                    failedCount = failedCount,
                    isUploadingPhoto = isUploadingPhoto,
                    onAddPhotoClick = onAddPhotoClick,
                    isReportingPhoto = isReportingPhoto,
                    isDeletingPhoto = isDeletingPhoto,
                    onReport = { pendingReportPhotoId = it },
                    onDelete = { pendingDeletePhotoId = it },
                    onOpenPhoto = { viewerStartPage = it },
                )
            }
        }
    }

    viewerStartPage?.let { startPage ->
        PhotoPagerViewer(
            photos = displayPhotos,
            startPage = startPage,
            onDismiss = { viewerStartPage = null },
        )
    }

    pendingReportPhotoId?.let { photoId ->
        PhotoReportConfirmationDialog(
            onDismiss = { pendingReportPhotoId = null },
            onConfirm = { onReportPhotoClick(photoId) },
        )
    }

    pendingDeletePhotoId?.let { photoId ->
        PhotoDeleteConfirmationDialog(
            onDismiss = { pendingDeletePhotoId = null },
            onConfirm = { onDeletePhotoClick(photoId) },
        )
    }
}

/** 1 photo (spec B1) : grande vignette 2fr + case « Ajouter » pleine hauteur. */
@Composable
private fun SinglePhotoRow(
    photo: TreeDetailPhoto,
    failedCount: Int,
    isUploadingPhoto: Boolean,
    onAddPhotoClick: (() -> Unit)?,
    isReportingPhoto: Boolean,
    isDeletingPhoto: Boolean,
    onReport: (String) -> Unit,
    onDelete: (String) -> Unit,
    onOpenPhoto: (Int) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().height(GRID_HEIGHT),
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        Box(Modifier.weight(2f).fillMaxHeight()) {
            PrimaryPhotoCell(
                photo = photo,
                totalCount = 1,
                isReportingPhoto = isReportingPhoto,
                isDeletingPhoto = isDeletingPhoto,
                onReport = onReport,
                onDelete = onDelete,
                onOpen = { onOpenPhoto(0) },
            )
        }
        if (failedCount > 0) {
            FailedPhotoTile(count = failedCount, modifier = Modifier.weight(1f).fillMaxHeight())
        } else if (onAddPhotoClick != null) {
            AddPhotoTile(
                isUploadingPhoto = isUploadingPhoto,
                onClick = onAddPhotoClick,
                modifier = Modifier.weight(1f).fillMaxHeight(),
            )
        }
    }
}

/** 2 photos et plus (spec B2/B3) : 2 colonnes × 2 rangées, photo 1 sur les deux rangées. */
@Composable
private fun MultiPhotoGrid(
    photos: List<TreeDetailPhoto>,
    failedCount: Int,
    isUploadingPhoto: Boolean,
    onAddPhotoClick: (() -> Unit)?,
    isReportingPhoto: Boolean,
    isDeletingPhoto: Boolean,
    onReport: (String) -> Unit,
    onDelete: (String) -> Unit,
    onOpenPhoto: (Int) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().height(CELL_HEIGHT * 2 + FruxSpacing.sm),
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        Box(Modifier.weight(1f).fillMaxHeight()) {
            PrimaryPhotoCell(
                photo = photos[0],
                totalCount = photos.size,
                isReportingPhoto = isReportingPhoto,
                isDeletingPhoto = isDeletingPhoto,
                onReport = onReport,
                onDelete = onDelete,
                onOpen = { onOpenPhoto(0) },
            )
        }
        Column(
            modifier = Modifier.weight(1f).fillMaxHeight(),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            PhotoCell(
                url = photos[1].url,
                contentDescription = stringResource(Res.string.treedetail_photo_index, 2, photos.size),
                onClick = { onOpenPhoto(1) },
                modifier = Modifier.weight(1f).fillMaxWidth(),
            )
            SecondaryBottomCell(
                photos = photos,
                failedCount = failedCount,
                isUploadingPhoto = isUploadingPhoto,
                onAddPhotoClick = onAddPhotoClick,
                onOpenViewer = { onOpenPhoto(2) },
            )
        }
    }
    if (photos.size >= 3 && failedCount == 0 && onAddPhotoClick != null) {
        Spacer(Modifier.height(FruxSpacing.sm))
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            FruxButton(
                text = stringResource(Res.string.treedetail_photo_add),
                onClick = onAddPhotoClick,
                style = FruxButtonStyle.Outline,
                loading = isUploadingPhoto,
                icon = Icons.Outlined.AddAPhoto,
            )
        }
    }
}

/** Case bas-droite de la grille 2+ : « Ajouter » à 2 photos, « +N / Voir toutes » à 3 et plus. */
@Composable
private fun ColumnScope.SecondaryBottomCell(
    photos: List<TreeDetailPhoto>,
    failedCount: Int,
    isUploadingPhoto: Boolean,
    onAddPhotoClick: (() -> Unit)?,
    onOpenViewer: () -> Unit,
) {
    when {
        photos.size >= 3 -> {
            MorePhotosCell(
                photo = photos[2],
                remainingCount = photos.size - 2,
                onClick = onOpenViewer,
                modifier = Modifier.weight(1f).fillMaxWidth(),
            )
        }

        failedCount > 0 -> {
            FailedPhotoTile(count = failedCount, modifier = Modifier.weight(1f).fillMaxWidth())
        }

        onAddPhotoClick != null -> {
            AddPhotoTile(
                isUploadingPhoto = isUploadingPhoto,
                onClick = onAddPhotoClick,
                modifier = Modifier.weight(1f).fillMaxWidth(),
            )
        }

        else -> {
            // Sans droit d'ajout ni 3e photo, la case reste vide : la 2e photo garde sa rangée.
            Spacer(Modifier.weight(1f))
        }
    }
}

/**
 * 0 photo réelle : bloc 240 dp `primaryContainer`, bouton terracotta bas-droite. Si l'espèce porte
 * une photo d'illustration (#389, ADR 0015 amendé), elle occupe le cadre en fond, avec la mention
 * permanente et le crédit ; sinon (ou si elle échoue à charger) le glyphe de l'espèce (repli park)
 * fait office de cadre mort, comme avant ce lot. Jamais de clic vers une visionneuse ni de
 * signalement sur cette photo : ce n'est pas une photo du domaine.
 */
@Composable
private fun EmptyPhotoBlock(
    speciesGlyph: ImageVector?,
    speciesPhoto: SpeciesPhoto?,
    speciesName: String,
    isUploadingPhoto: Boolean,
    onAddPhotoClick: (() -> Unit)?,
) {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .height(GRID_HEIGHT)
                .clip(RoundedCornerShape(GRID_RADIUS))
                .background(MaterialTheme.colorScheme.primaryContainer),
    ) {
        val a11yLabel = stringResource(Res.string.treedetail_species_photo_a11y, speciesName)
        val a11yCredit =
            speciesPhoto?.let {
                val author = it.author
                if (author != null) {
                    stringResource(Res.string.treedetail_species_photo_a11y_credit_author, author, it.source)
                } else {
                    stringResource(Res.string.treedetail_species_photo_a11y_credit_source, it.source)
                }
            }
        FruxRemoteImage(
            url = speciesPhoto?.url,
            contentDescription = if (speciesPhoto != null) "$a11yLabel. $a11yCredit" else null,
            contentScale = ContentScale.Crop,
            modifier = Modifier.fillMaxSize(),
            fallback = {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(
                        modifier = Modifier.padding(bottom = 40.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                    ) {
                        Icon(
                            imageVector = speciesGlyph ?: Icons.Outlined.Park,
                            contentDescription = null,
                            modifier = Modifier.size(72.dp),
                            tint = MaterialTheme.colorScheme.primary,
                        )
                        Text(
                            text = stringResource(Res.string.treedetail_photo_empty),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.secondary,
                        )
                    }
                }
            },
            successOverlay = {
                if (speciesPhoto != null) {
                    SpeciesPhotoCaption(photo = speciesPhoto)
                }
            },
        )
        if (onAddPhotoClick != null) {
            Surface(
                onClick = onAddPhotoClick,
                enabled = !isUploadingPhoto,
                shape = RoundedCornerShape(50),
                color = MaterialTheme.fruxColors.accent,
                contentColor = MaterialTheme.fruxColors.onAccent,
                shadowElevation = 2.dp,
                modifier =
                    Modifier
                        .align(Alignment.BottomEnd)
                        .padding(12.dp)
                        .height(48.dp),
            ) {
                Row(
                    modifier = Modifier.padding(start = FruxSpacing.md, end = 20.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    if (isUploadingPhoto) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(20.dp),
                            strokeWidth = 2.dp,
                            color = MaterialTheme.fruxColors.onAccent,
                        )
                    } else {
                        Icon(
                            imageVector = Icons.Outlined.AddAPhoto,
                            contentDescription = null,
                            modifier = Modifier.size(20.dp),
                        )
                    }
                    Text(stringResource(Res.string.treedetail_photo_add), style = MaterialTheme.typography.labelLarge)
                }
            }
        }
    }
}

/**
 * Pastille de mention + crédit sur la photo d'espèce (#389, spec §5.3) : fond sombre semi-opaque,
 * en haut à gauche pour ne jamais chevaucher le bouton « Ajouter une photo » en bas à droite.
 * Retirée de l'arbre sémantique (`clearAndSetSemantics {}`, jamais `hideFromAccessibility`, sans
 * effet sur js) : le lecteur d'écran lit déjà la mention et le crédit via la `contentDescription`
 * de l'image porteuse, les lire une seconde fois ici serait redondant.
 */
@Composable
private fun BoxScope.SpeciesPhotoCaption(photo: SpeciesPhoto) {
    Column(
        modifier =
            Modifier
                .align(Alignment.TopStart)
                .padding(FruxSpacing.sm)
                .clip(RoundedCornerShape(10.dp))
                .background(Color.Black.copy(alpha = 0.55f))
                .padding(horizontal = FruxSpacing.sm, vertical = 6.dp)
                .clearAndSetSemantics {},
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Text(
            text = stringResource(Res.string.treedetail_species_photo_caption),
            style = MaterialTheme.typography.labelMedium,
            color = Color.White,
        )
        val author = photo.author
        Text(
            text =
                if (author != null) {
                    stringResource(Res.string.treedetail_species_photo_credit_author, author, photo.source)
                } else {
                    stringResource(Res.string.treedetail_species_photo_credit_source, photo.source)
                },
            style = MaterialTheme.typography.labelSmall,
            color = Color.White,
        )
    }
}

/** Grande vignette, seule à porter l'action drapeau/corbeille (une seule, selon les droits). */
@Composable
private fun BoxScope.PrimaryPhotoCell(
    photo: TreeDetailPhoto,
    totalCount: Int,
    isReportingPhoto: Boolean,
    isDeletingPhoto: Boolean,
    onReport: (String) -> Unit,
    onDelete: (String) -> Unit,
    onOpen: () -> Unit,
) {
    PhotoCell(
        url = photo.url,
        contentDescription =
            if (totalCount > 1) {
                stringResource(Res.string.treedetail_photo_index, 1, totalCount)
            } else {
                stringResource(Res.string.treedetail_photo_single_description)
            },
        onClick = onOpen,
        modifier = Modifier.fillMaxSize(),
    )
    when {
        photo.canDelete -> {
            PhotoOverlayAction(
                icon = Icons.Outlined.Delete,
                contentDescription = stringResource(Res.string.treedetail_photo_delete_description),
                isPending = isDeletingPhoto,
                onClick = { onDelete(photo.id) },
            )
        }

        photo.canReport -> {
            PhotoOverlayAction(
                icon = Icons.Outlined.Flag,
                contentDescription = stringResource(Res.string.treedetail_photo_report_description),
                isPending = isReportingPhoto,
                onClick = { onReport(photo.id) },
            )
        }
    }
}

/**
 * Une photo de la grille, coins 20 dp, fond `primaryContainer` le temps du chargement. [onClick]
 * ouvre la visionneuse sur cette photo (#686).
 */
@Composable
private fun PhotoCell(
    url: String,
    contentDescription: String,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
) {
    val openLabel = stringResource(Res.string.treedetail_photo_open_description)
    Box(
        modifier =
            modifier
                .clip(RoundedCornerShape(GRID_RADIUS))
                .background(MaterialTheme.colorScheme.primaryContainer)
                .then(
                    if (onClick != null) Modifier.clickable(onClickLabel = openLabel, onClick = onClick) else Modifier,
                ),
    ) {
        FruxRemoteImage(
            url = url,
            contentDescription = contentDescription,
            contentScale = ContentScale.Crop,
            modifier = Modifier.fillMaxSize(),
            fallback = {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = Icons.Outlined.Park,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onPrimaryContainer,
                        modifier = Modifier.size(40.dp),
                    )
                }
            },
        )
    }
}

/** 3e case : la 3e photo sous un voile `primary` 55 %, « +N », ouvre le pager sur cette 3e photo. */
@Composable
private fun MorePhotosCell(
    photo: TreeDetailPhoto,
    remainingCount: Int,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val viewAllDescription = stringResource(Res.string.treedetail_photo_view_all_description)
    Box(
        modifier =
            modifier
                .clip(RoundedCornerShape(GRID_RADIUS))
                .clickable(onClick = onClick)
                .semantics { contentDescription = viewAllDescription },
    ) {
        PhotoCell(url = photo.url, contentDescription = "", modifier = Modifier.fillMaxSize())
        Column(
            modifier =
                Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.55f)),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Text(
                text = stringResource(Res.string.treedetail_photo_more_count, remainingCount),
                style = MaterialTheme.typography.displaySmall,
                color = MaterialTheme.colorScheme.onPrimary,
            )
            Text(
                text = viewAllDescription,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onPrimary,
            )
        }
    }
}

/** Case « Ajouter une photo » : `tertiaryContainer`, bordure pointillée accent (spec B1/B2). */
@Composable
private fun AddPhotoTile(
    isUploadingPhoto: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val accent = MaterialTheme.fruxColors.accent
    val addPhotoLabel = stringResource(Res.string.treedetail_photo_add)
    Column(
        modifier =
            modifier
                .clip(RoundedCornerShape(GRID_RADIUS))
                .background(MaterialTheme.colorScheme.tertiaryContainer)
                .drawBehind {
                    drawRoundRect(
                        color = accent,
                        cornerRadius = CornerRadius(GRID_RADIUS.toPx()),
                        style =
                            Stroke(
                                width = 1.dp.toPx(),
                                pathEffect = PathEffect.dashPathEffect(floatArrayOf(8f, 8f)),
                            ),
                    )
                }.clickable(enabled = !isUploadingPhoto, onClick = onClick)
                .semantics { contentDescription = addPhotoLabel }
                .padding(FruxSpacing.sm),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(6.dp, Alignment.CenterVertically),
    ) {
        if (isUploadingPhoto) {
            CircularProgressIndicator(
                modifier = Modifier.size(28.dp),
                strokeWidth = 2.dp,
                color = accent,
            )
        } else {
            Icon(
                imageVector = Icons.Outlined.AddAPhoto,
                contentDescription = null,
                modifier = Modifier.size(28.dp),
                tint = accent,
            )
        }
        Text(
            text = addPhotoLabel,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onTertiaryContainer,
        )
    }
}

/** Action unique de la grande vignette : icône 40 dp sur voile sombre 35 % (spec B). */
@Composable
private fun BoxScope.PhotoOverlayAction(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    contentDescription: String,
    isPending: Boolean,
    onClick: () -> Unit,
) {
    IconButton(
        onClick = onClick,
        enabled = !isPending,
        modifier =
            Modifier
                .align(Alignment.TopEnd)
                .padding(FruxSpacing.sm)
                .size(40.dp)
                .clip(CircleShape)
                // Voile teinté encre, identique clair/sombre : il vit sur une photo, pas sur une
                // surface du thème — le blanc de l'icône reste lisible dans les deux thèmes.
                .background(Color.Black.copy(alpha = 0.35f)),
    ) {
        if (isPending) {
            CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp, color = Color.White)
        } else {
            Icon(
                imageVector = icon,
                contentDescription = contentDescription,
                tint = Color.White,
                modifier = Modifier.size(20.dp),
            )
        }
    }
}

/**
 * Pager plein écran « Voir toutes les photos » (spec B3). Les points de pagination restent
 * cliquables (souris, clavier, lecteur d'écran) — même écart assumé au M3 canonique que l'ancien
 * hero pager (#247). Chaque photo se zoome (#686) sans rien retirer au lecteur d'écran : fermeture
 * et pagination restent les mêmes.
 */
@Composable
private fun PhotoPagerViewer(
    photos: List<TreeDetailPhoto>,
    startPage: Int,
    onDismiss: () -> Unit,
) {
    val pagerState = rememberPagerState(initialPage = startPage, pageCount = { photos.size })
    val scope = rememberCoroutineScope()
    val reduceMotion = rememberReduceMotion()
    val zoomStates = remember(photos.size) { List(photos.size) { ZoomState(animate = !reduceMotion) } }
    // Changer de photo remet les autres à 1x : on ne retrouve jamais une photo zoomée en revenant.
    LaunchedEffect(pagerState.settledPage) {
        zoomStates.forEachIndexed { page, state -> if (page != pagerState.settledPage) state.reset() }
    }
    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Box(
            modifier =
                Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.9f)),
        ) {
            HorizontalPager(
                state = pagerState,
                modifier = Modifier.fillMaxSize(),
                // Zoomée, la photo garde le glisser pour le déplacement : le pager ne le prend qu'à 1x.
                userScrollEnabled = photos.size > 1 && !zoomStates[pagerState.currentPage].isZoomed,
            ) { page ->
                val photo = photos[page]
                val zoomState = zoomStates[page]
                FruxRemoteImage(
                    url = photo.fullUrl,
                    contentDescription = stringResource(Res.string.treedetail_photo_index, page + 1, photos.size),
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxSize().zoomable(zoomState),
                    // La miniature, déjà en cache depuis la grille, tient la place le temps du chargement.
                    fallback = {
                        FruxRemoteImage(
                            url = photo.url,
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.fillMaxSize(),
                        )
                    },
                    onIntrinsicSize = zoomState::setIntrinsicSize,
                    decodeAtOriginalSize = true,
                )
            }
            IconButton(
                onClick = onDismiss,
                modifier =
                    Modifier
                        .align(Alignment.TopEnd)
                        .padding(FruxSpacing.sm)
                        .size(48.dp),
            ) {
                Icon(
                    imageVector = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.treedetail_photo_close_description),
                    tint = Color.White,
                )
            }
            if (photos.size > 1) {
                Row(
                    modifier =
                        Modifier
                            .align(Alignment.BottomCenter)
                            .padding(bottom = FruxSpacing.md),
                    horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    repeat(photos.size) { page ->
                        val selected = page == pagerState.currentPage
                        // Capturé en composition : `.semantics {}` n'est pas un contexte composable.
                        val pageDescription =
                            stringResource(Res.string.treedetail_photo_view_page_description, page + 1)
                        Box(
                            modifier =
                                Modifier
                                    .size(if (selected) 10.dp else 8.dp)
                                    .clip(CircleShape)
                                    .background(if (selected) Color.White else Color.White.copy(alpha = 0.4f))
                                    .clickable(onClick = { scope.launch { pagerState.animateScrollToPage(page) } })
                                    .semantics { contentDescription = pageDescription },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PhotoReportConfirmationDialog(
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.treedetail_photo_report_dialog_title)) },
        text = { Text(stringResource(Res.string.treedetail_photo_report_dialog_text)) },
        confirmButton = {
            TextButton(
                onClick = {
                    onDismiss()
                    onConfirm()
                },
            ) {
                Text(stringResource(Res.string.treedetail_photo_report_dialog_confirm))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.treedetail_photo_report_dialog_cancel))
            }
        },
    )
}

@Composable
private fun PhotoDeleteConfirmationDialog(
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.treedetail_photo_delete_dialog_title)) },
        text = { Text(stringResource(Res.string.treedetail_photo_delete_dialog_text)) },
        confirmButton = {
            TextButton(
                onClick = {
                    onDismiss()
                    onConfirm()
                },
            ) {
                Text(stringResource(Res.string.treedetail_photo_delete_dialog_confirm))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.treedetail_photo_delete_dialog_cancel))
            }
        },
    )
}
